'use client';
import React, { useState, useEffect, useCallback } from 'react';
import {
  X, Download, ChevronRight, Zap, WifiOff, Bell, RefreshCw,
  ShoppingBag, Briefcase, Home, Star, ArrowRight, CheckCircle,
} from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const PAGE_VISIT_KEY = 'cn_page_visits';
const DISMISSED_KEY = 'cn_pwa_dismissed';
const INSTALLED_KEY = 'cn_pwa_installed';
const UPDATE_DISMISSED_KEY = 'cn_update_dismissed';
const ONBOARDING_KEY = 'cn_pwa_onboarding_done';
const DISMISS_COOLDOWN_MS = 7 * 24 * 3600000; // 7 days
const MIN_VISITS = 3;
const SHOW_DELAY_MS = 8000;

// ─── Update Prompt ─────────────────────────────────────────────────────────────
export function PWAUpdatePrompt() {
  const [show, setShow] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState<ServiceWorker | null>(null);
  const [reloading, setReloading] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;

    let controllerChangeHandled = false;

    navigator.serviceWorker.ready.then((reg) => {
      const checkWaiting = () => {
        if (reg.waiting && navigator.serviceWorker.controller) {
          setWaitingWorker(reg.waiting);
          setShow(true);
        }
      };

      // Check immediately
      checkWaiting();

      reg.addEventListener('updatefound', () => {
        const newWorker = reg.installing;
        if (!newWorker) return;
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            setWaitingWorker(newWorker);
            setShow(true);
          }
        });
      });
    });

    // Listen for controller change — reload once, guard against loops
    const onControllerChange = () => {
      if (controllerChangeHandled) return;
      controllerChangeHandled = true;
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);
    return () => {
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
    };
  }, []);

  const handleUpdate = () => {
    if (!waitingWorker || reloading) return;
    setReloading(true);
    waitingWorker.postMessage({ type: 'SKIP_WAITING' });
    setShow(false);
  };

  const handleLater = () => {
    setShow(false);
    try { localStorage.setItem(UPDATE_DISMISSED_KEY, Date.now().toString()); } catch {}
  };

  if (!show) return null;

  return (
    <div className="fixed top-4 left-4 right-4 z-[200] max-w-sm mx-auto" style={{ animation: 'slideInFromTop 0.3s ease-out' }}>
      <div className="rounded-2xl p-4 shadow-2xl" style={{ background: '#0D1526', border: '1.5px solid rgba(200,16,46,0.5)', boxShadow: '0 8px 40px rgba(200,16,46,0.2)' }}>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(200,16,46,0.15)' }}>
            <RefreshCw size={18} style={{ color: '#c8102e' }} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-bold text-white">New version available</h3>
            <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.55)' }}>
              China Ndogo has been updated with new features and improvements.
            </p>
          </div>
          <button onClick={handleLater} className="p-1 rounded-lg flex-shrink-0" style={{ background: 'rgba(255,255,255,0.06)' }} aria-label="Dismiss">
            <X size={12} style={{ color: 'rgba(255,255,255,0.4)' }} />
          </button>
        </div>
        <div className="flex gap-2 mt-3">
          <button onClick={handleLater} className="flex-1 py-2 rounded-xl text-xs font-semibold" style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.5)' }}>
            Later
          </button>
          <button onClick={handleUpdate} disabled={reloading} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-bold" style={{ background: '#c8102e', color: '#fff', opacity: reloading ? 0.7 : 1 }}>
            <RefreshCw size={12} className={reloading ? 'animate-spin' : ''} />
            {reloading ? 'Updating…' : 'Update Now'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Post-Install Onboarding ───────────────────────────────────────────────────
export function PWAOnboarding({ onDone }: { onDone: () => void }) {
  const [step, setStep] = useState(0);

  const steps = [
    {
      icon: <ShoppingBag size={36} style={{ color: '#c8102e' }} />,
      title: 'Welcome to China Ndogo!',
      body: 'Your marketplace for products, jobs, properties, hotels and services — now on your home screen.',
      cta: 'Get Started',
    },
    {
      icon: <Briefcase size={36} style={{ color: '#c8102e' }} />,
      title: 'Browse & Post Listings',
      body: 'Find great deals or sell your own items. Post a listing in under 2 minutes.',
      cta: 'Next',
    },
    {
      icon: <Bell size={36} style={{ color: '#c8102e' }} />,
      title: 'Stay in the Loop',
      body: 'Enable notifications to get instant alerts on messages, offers, and order updates.',
      cta: 'Next',
    },
    {
      icon: <WifiOff size={36} style={{ color: '#c8102e' }} />,
      title: 'Works Offline Too',
      body: 'Browse previously loaded listings even without internet. Your data stays safe.',
      cta: 'Start Exploring',
    },
  ];

  const current = steps[step];
  const isLast = step === steps.length - 1;

  const handleNext = () => {
    if (isLast) {
      try { localStorage.setItem(ONBOARDING_KEY, '1'); } catch {}
      onDone();
    } else {
      setStep((s) => s + 1);
    }
  };

  return (
    <div className="fixed inset-0 z-[300] flex items-end justify-center" style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)' }}>
      <div className="w-full max-w-sm rounded-t-3xl p-6 pb-10" style={{ background: '#0D1526', border: '1.5px solid rgba(200,16,46,0.3)' }}>
        {/* Progress dots */}
        <div className="flex justify-center gap-2 mb-6">
          {steps.map((_, i) => (
            <div key={i} className="h-1.5 rounded-full transition-all duration-300" style={{ width: i === step ? 24 : 8, background: i === step ? '#c8102e' : 'rgba(255,255,255,0.15)' }} />
          ))}
        </div>

        {/* Icon */}
        <div className="flex justify-center mb-5">
          <div className="w-20 h-20 rounded-3xl flex items-center justify-center" style={{ background: 'rgba(200,16,46,0.1)', border: '1.5px solid rgba(200,16,46,0.2)' }}>
            {current.icon}
          </div>
        </div>

        {/* Text */}
        <h2 className="text-xl font-bold text-white text-center mb-2">{current.title}</h2>
        <p className="text-sm text-center leading-relaxed mb-8" style={{ color: 'rgba(255,255,255,0.55)' }}>{current.body}</p>

        {/* CTA */}
        <button onClick={handleNext} className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl font-bold text-sm" style={{ background: '#c8102e', color: '#fff' }}>
          {current.cta}
          {isLast ? <CheckCircle size={16} /> : <ArrowRight size={16} />}
        </button>

        {/* Skip */}
        {!isLast && (
          <button onClick={() => { try { localStorage.setItem(ONBOARDING_KEY, '1'); } catch {} onDone(); }} className="w-full mt-3 py-2 text-xs font-semibold" style={{ color: 'rgba(255,255,255,0.35)' }}>
            Skip
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Install Banner ────────────────────────────────────────────────────────────
export default function PWAInstallPrompt() {
  const [show, setShow] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  const shouldShow = useCallback((hasPrompt: boolean) => {
    if (typeof window === 'undefined') return false;
    try {
      if (window.matchMedia('(display-mode: standalone)').matches) return false;
      if (localStorage.getItem(INSTALLED_KEY)) return false;
      const dismissed = localStorage.getItem(DISMISSED_KEY);
      if (dismissed && Date.now() - parseInt(dismissed) < DISMISS_COOLDOWN_MS) return false;
      const visits = parseInt(localStorage.getItem(PAGE_VISIT_KEY) || '0');
      if (visits < MIN_VISITS) return false;
      return true;
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Detect standalone mode
    const standalone = window.matchMedia('(display-mode: standalone)').matches;
    setIsStandalone(standalone);

    // Show onboarding if just installed and not done yet
    if (standalone) {
      try {
        const onboardingDone = localStorage.getItem(ONBOARDING_KEY);
        const justInstalled = localStorage.getItem(INSTALLED_KEY);
        if (justInstalled && !onboardingDone) {
          setShowOnboarding(true);
        }
      } catch {}
      return;
    }

    // Increment visit count
    try {
      const visits = parseInt(localStorage.getItem(PAGE_VISIT_KEY) || '0');
      localStorage.setItem(PAGE_VISIT_KEY, String(visits + 1));
    } catch {}

    const handler = (e: Event) => {
      e.preventDefault();
      const prompt = e as BeforeInstallPromptEvent;
      setDeferredPrompt(prompt);
      if (shouldShow(true)) {
        setTimeout(() => setShow(true), SHOW_DELAY_MS);
      }
    };

    window.addEventListener('beforeinstallprompt', handler);

    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setShow(false);
      try { localStorage.setItem(INSTALLED_KEY, '1'); } catch {}
      // Show onboarding after install
      setTimeout(() => setShowOnboarding(true), 1000);
    });

    // iOS Safari: show after delay if enough visits (no beforeinstallprompt)
    const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
    if (isIOS && shouldShow(false)) {
      setTimeout(() => setShow(true), SHOW_DELAY_MS);
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, [shouldShow]);

  const handleInstall = async () => {
    if (deferredPrompt) {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setIsInstalled(true);
        try { localStorage.setItem(INSTALLED_KEY, '1'); } catch {}
      }
      setDeferredPrompt(null);
      setShow(false);
    } else {
      // iOS Safari fallback
      setShowHowItWorks(true);
    }
  };

  const handleDismiss = () => {
    try { localStorage.setItem(DISMISSED_KEY, Date.now().toString()); } catch {}
    setShow(false);
  };

  // Don't render install prompt in standalone mode or after install
  if (isStandalone || isInstalled) {
    return showOnboarding ? <PWAOnboarding onDone={() => setShowOnboarding(false)} /> : null;
  }

  return (
    <>
      {showOnboarding && <PWAOnboarding onDone={() => setShowOnboarding(false)} />}

      {show && (
        <div
          className="fixed bottom-20 left-3 right-3 z-[60] max-w-sm mx-auto"
          style={{ animation: 'slideInFromBottom 0.35s cubic-bezier(0.34,1.56,0.64,1)' }}
          role="dialog"
          aria-label="Install China Ndogo app"
        >
          <div className="rounded-2xl p-4 shadow-2xl relative" style={{ background: '#0D1526', border: '1.5px solid rgba(200,16,46,0.4)', boxShadow: '0 -4px 40px rgba(200,16,46,0.18), 0 8px 40px rgba(0,0,0,0.5)' }}>
            {/* Close */}
            <button onClick={handleDismiss} className="absolute top-3 right-3 w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.06)' }} aria-label="Dismiss install prompt">
              <X size={13} style={{ color: 'rgba(255,255,255,0.4)' }} />
            </button>

            {/* Header */}
            <div className="flex items-start gap-3 mb-4 pr-8">
              <div className="w-14 h-14 rounded-2xl overflow-hidden flex-shrink-0 border-2" style={{ borderColor: 'rgba(200,16,46,0.4)' }}>
                <img
                  src="/assets/images/china-ndogo-icon-512.png"
                  alt="China Ndogo app icon"
                  className="w-full h-full object-cover"
                  onError={(e) => { (e.target as HTMLImageElement).src = '/assets/images/app_logo.png'; }}
                />
              </div>
              <div>
                <h3 className="font-bold text-base text-white leading-tight">Install China Ndogo</h3>
                <p className="text-xs mt-1 leading-relaxed" style={{ color: 'rgba(255,255,255,0.55)' }}>
                  Shop, find jobs, book hotels & services — faster from your home screen.
                </p>
              </div>
            </div>

            {/* Feature pills */}
            <div className="flex gap-2 mb-4 flex-wrap">
              {[
                { icon: <Zap size={11} />, label: 'Faster loading' },
                { icon: <WifiOff size={11} />, label: 'Works offline' },
                { icon: <Bell size={11} />, label: 'Instant alerts' },
                { icon: <Star size={11} />, label: 'No app store' },
              ].map((f) => (
                <div key={f.label} className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold" style={{ background: 'rgba(200,16,46,0.1)', border: '1px solid rgba(200,16,46,0.2)', color: 'rgba(255,255,255,0.75)' }}>
                  <span style={{ color: '#c8102e' }}>{f.icon}</span>
                  {f.label}
                </div>
              ))}
            </div>

            {/* iOS How It Works */}
            {showHowItWorks && (
              <div className="mb-3 p-3 rounded-xl text-xs leading-relaxed" style={{ background: 'rgba(200,16,46,0.06)', border: '1px solid rgba(200,16,46,0.2)', color: 'rgba(255,255,255,0.6)' }}>
                <p className="font-bold text-white mb-1.5">Install on iPhone / iPad:</p>
                <p className="mb-0.5">1. Tap the <strong style={{ color: '#c8102e' }}>Share</strong> button (□↑) in Safari</p>
                <p className="mb-0.5">2. Scroll and tap <strong style={{ color: '#c8102e' }}>"Add to Home Screen"</strong></p>
                <p>3. Tap <strong style={{ color: '#c8102e' }}>"Add"</strong> to confirm</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-2">
              <button onClick={handleDismiss} className="py-2.5 px-3 rounded-xl text-xs font-semibold flex-shrink-0" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.45)' }}>
                Not Now
              </button>
              <button onClick={() => setShowHowItWorks(!showHowItWorks)} className="flex items-center gap-1 px-3 py-2.5 rounded-xl text-xs font-semibold flex-shrink-0" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.45)' }}>
                How? <ChevronRight size={11} />
              </button>
              <button onClick={handleInstall} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-bold" style={{ background: 'linear-gradient(135deg, #c8102e, #a00d24)', color: '#fff', boxShadow: '0 4px 16px rgba(200,16,46,0.4)' }}>
                <Download size={13} />
                Install App
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideInFromBottom {
          from { opacity: 0; transform: translateY(24px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes slideInFromTop {
          from { opacity: 0; transform: translateY(-16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </>
  );
}
